/**
 * ProductRecipeDrawer — bir ürünün reçetesini (BOM) ürün listesinden
 * ayrılmadan görüntüleyip düzenlemek için yan panel.
 *
 * - Ürünün reçetesi varsa: BomItemEditor ile malzemeleri gösterir/düzenler
 * - Reçetesi yoksa: tek tıkla "Reçete Oluştur"
 *
 * Reçete (BOM) sayfasındaki kart mantığının ürün-bağlamlı bir kopyası;
 * orada da aynı BomItemEditor kullanılır.
 */
import { Badge, Button, Drawer, Group, Loader, Stack, Text } from "@mantine/core";
import { IconChefHat, IconPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useResource, useCreate } from "../../../shared/api";
import { BomItemEditor } from "./BomItemEditor";

interface Props {
  /** Reçetesi gösterilecek ürün — null ise drawer kapalı. */
  product: { id: string; name: string } | null;
  onClose: () => void;
}

export function ProductRecipeDrawer({ product, onClose }: Props) {
  const qc = useQueryClient();
  // boms'u product filtresiyle çek — yalnız bu ürünün reçetesi.
  const { data: bomData, isLoading: loading } = useResource<any>("boms", {
    ...(product ? { filters: { product: product.id } } : {}),
    enabled: !!product,
  });
  const bomCreate = useCreate("boms");

  const boms: any[] = bomData?.results || bomData || [];
  const bom = boms[0] || null;

  async function handleCreateBom() {
    if (!product) return;
    try {
      await bomCreate.mutateAsync({ product: product.id, is_active: true });
      qc.invalidateQueries({ queryKey: ["boms"] });
      notifications.show({ message: "Reçete oluşturuldu", color: "green" });
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail
          || e?.response?.data?.product?.[0]
          || "Reçete oluşturulamadı",
        color: "red",
      });
    }
  }

  return (
    <Drawer
      opened={!!product}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        <Group gap={8}>
          <IconChefHat size={20} />
          <Text fw={600}>{product ? `${product.name} — Reçete` : "Reçete"}</Text>
        </Group>
      }
    >
      {loading ? (
        <Group justify="center" py="xl"><Loader /></Group>
      ) : !bom ? (
        // Reçete yok — oluşturma çağrısı
        <Stack gap="md" align="center" py="xl">
          <IconChefHat size={48} color="var(--mantine-color-gray-4)" />
          <Text c="dimmed" ta="center">
            Bu ürün için henüz reçete tanımlanmamış.
            <br />
            Reçete oluşturup malzeme ekleyin — maliyet ve marj otomatik hesaplanır.
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateBom}
            loading={bomCreate.isPending}
          >
            Reçete Oluştur
          </Button>
        </Stack>
      ) : (
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="sm" c="dimmed">{bom.notes || "Reçete malzemeleri"}</Text>
            {bom.is_active
              ? <Badge color="green" size="sm">Aktif</Badge>
              : <Badge color="gray" size="sm">Pasif</Badge>}
          </Group>

          <BomItemEditor bomId={bom.id} items={bom.items || []} />
        </Stack>
      )}
    </Drawer>
  );
}
